import React, { useEffect, useRef, useState } from "react";
import photo1 from "../../Assets/Images/landing/photo-1.jpg";
import photo2 from "../../Assets/Images/landing/photo-2.jpg";
import photo3 from "../../Assets/Images/landing/photo-3.jpg";
import photo4 from "../../Assets/Images/landing/photo-4.jpg";
import photo5 from "../../Assets/Images/landing/photo-5.jpg";
import photo6 from "../../Assets/Images/landing/photo-6.jpg";
import logoMark from "../../Assets/Images/landing/eww-logo.png";

type Visual = "single" | "batch" | "templates" | "sync" | "sticker" | "signature" | "fonts" | "logo" | "mobile";

const keyFeatures: { tag: string; title: string; body: string; photo: string; visual: Visual }[] = [
  {
    tag: "Watermark Image",
    title: "Add watermark to your photos",
    body: "Add watermarks to your photos, ensuring their security and enhancing their professional appearance. Whether you're a professional photographer or an enthusiastic hobbyist, elevate your images with just a few simple steps.",
    photo: photo1,
    visual: "single",
  },
  {
    tag: "Batch watermarking",
    title: "Watermark a batch of photos",
    body: "Apply watermarks to an entire batch of photos with just a few clicks. Watermark hundreds of photos quickly in one go. Manually reposition and resize watermarks across landscape and portrait shots.",
    photo: photo4,
    visual: "batch",
  },
  {
    tag: "Template creation",
    title: "Create your own templates",
    body: "Design your own templates to conveniently store watermarks and their positions, ensuring consistency across your photos. Apply templates to automatically place watermarks on multiple photos.",
    photo: photo3,
    visual: "templates",
  },
  {
    tag: "Template syncing",
    title: "Sync templates across devices",
    body: "Sign in once and your templates travel with you. Create a watermark layout on your phone, then pick up the same template on your tablet or the web. Every saved design syncs automatically to your account, so you can watermark from any device without starting over.",
    // Not rendered for "sync" (its visual is a custom two-device composition, no single
    // background photo) -- kept only because every entry needs one for the shared type.
    photo: photo2,
    visual: "sync",
  },
  {
    tag: "Creative touch",
    title: "Add sticker watermarks",
    body: "Add a touch of creativity, fun, and engagement. Choose from a wide selection of stickers and place them strategically on your photos — playful icons, promotional graphics, or custom designs.",
    photo: photo5,
    visual: "sticker",
  },
  {
    tag: "Add signature",
    title: "Add signature watermarks",
    body: "Leave a lasting impression on your photos with a signature watermark. Personalize your images with your unique signature and establish a professional touch on every image you share.",
    photo: photo2,
    visual: "signature",
  },
  {
    tag: "Multiple fonts",
    title: "More than 150 fonts",
    body: "Select from a vast collection of over 150 fonts. From elegant and classic to modern and bold, explore a wide variety of fonts to add a personalized touch to your watermarked photos.",
    photo: photo6,
    visual: "fonts",
  },
  {
    tag: "Logo watermark",
    title: "Add personalised logos on photos",
    body: "Safeguard your valuable photos by imprinting your logo — establish brand recognition and ensure your visual content remains protected with an extra layer of professionalism.",
    photo: photo3,
    visual: "logo",
  },
  {
    tag: "iOS & Android",
    title: "Watermark photos on mobile apps",
    body: "Add watermarks to your photos using our mobile applications. Seamlessly watermark photos in one go directly from your device — anytime, anywhere.",
    photo: photo1,
    visual: "mobile",
  },
];

const FeatureVisual: React.FC<{ variant: Visual; photo: string }> = ({ variant, photo }) => {
  return (
    <div className={`fp-visual ${variant === "sync" ? "fp-visual--sync" : ""}`}>
      {/* "sync" shows two separate device mockups instead of one full-bleed photo. */}
      {variant !== "sync" && (
        <>
          <img src={photo} alt="" />
          <div className="fp-visual__scrim" />
        </>
      )}

      {variant === "single" && (
        <>
          <div className="fp-visual__tile" />
          <div className="fp-visual__protected-badge">© PROTECTED</div>
        </>
      )}

      {variant === "batch" && (
        <div className="fp-visual__batch">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="fp-visual__batch-thumb">
              <span style={{ animationDelay: `${i * 0.4}s` }}>© eZy</span>
            </div>
          ))}
          <div className="fp-visual__batch-bar">
            <div />
          </div>
        </div>
      )}

      {variant === "templates" && (
        <div className="fp-visual__templates">
          <div className="fp-visual__tpl fp-visual__tpl--1">© top-left</div>
          <div className="fp-visual__tpl fp-visual__tpl--2">© center</div>
          <div className="fp-visual__tpl fp-visual__tpl--3">© bottom-right</div>
        </div>
      )}

      {variant === "sync" && (
        <div className="fp-visual__sync">
          <div className="fp-visual__sync-devices">
            <div className="fp-visual__sync-device fp-visual__sync-device--phone">
              <img src={photo2} alt="" />
              <span>iPhone</span>
            </div>
            <div className="fp-visual__sync-hub">
              <span className="fp-visual__sync-hub-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                  <path d="M16 21h5v-5" />
                </svg>
              </span>
              <span className="fp-visual__sync-hub-label">Auto-sync</span>
            </div>
            <div className="fp-visual__sync-device fp-visual__sync-device--tablet">
              <img src={photo4} alt="" />
              <span>iPad</span>
            </div>
          </div>
          <div className="fp-visual__sync-beam" />
          <div className="fp-visual__sync-card">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}

      {variant === "sticker" && (
        <>
          <span className="fp-visual__sticker fp-visual__sticker--1">✨</span>
          <span className="fp-visual__sticker fp-visual__sticker--2">📷</span>
          <span className="fp-visual__sticker fp-visual__sticker--3">💫</span>
          <div className="fp-visual__chip fp-visual__chip--sticker">Sticker · Playful</div>
        </>
      )}

      {variant === "signature" && (
        <div className="fp-visual__signature">
          {/* A cursive two-letter monogram (initials-style) traced as one pen stroke per
              letter and "drawn" live via stroke-dashoffset -- pathLength="100" normalizes the
              dash values regardless of exact path geometry. The lone moveto mid-path is a
              deliberate pen-lift between the two letters, same as real handwriting. The tail
              after the second letter swoops below the underline, like a real signature
              flourish. Shifted right via the <g> transform, with the underline shortened to
              match the letters' actual width instead of stretching the full viewBox. */}
          <svg className="fp-visual__signature-svg" viewBox="0 0 155 70" fill="none">
            <g transform="translate(20, 0)">
              <path
                pathLength="100"
                d="M8 50C10 30 16 15 22 15C28 15 30 32 26 38C24 41 20 39 21 34C23 26 30 16 36 19C40 21 38 34 33 50M46 50L46 16C52 12 64 13 65 19C66 25 55 27 50 24C55 30 65 40 74 50C84 60 96 68 108 60C116 55 122 62 130 56"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                pathLength="100"
                d="M10 58c20 3 90 4 118-1"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.7"
              />
            </g>
          </svg>
        </div>
      )}

      {variant === "fonts" && (
        <div className="fp-visual__fonts">
          <span className="fp-visual__chip" style={{ fontFamily: "serif", animationDelay: "0s" }}>Aa Serif</span>
          <span className="fp-visual__chip" style={{ fontFamily: "monospace", animationDelay: "0.6s" }}>Aa Mono</span>
          <span className="fp-visual__chip" style={{ fontFamily: "cursive", animationDelay: "1.2s" }}>Aa Script</span>
          <span className="fp-visual__chip" style={{ fontWeight: 900, animationDelay: "1.8s" }}>Aa Bold</span>
        </div>
      )}

      {variant === "logo" && (
        <div className="fp-visual__logo-badge">
          <img src={logoMark} alt="eZy logo watermark" />
        </div>
      )}

      {variant === "mobile" && (
        <div className="fp-visual__phone-wrap">
          <div className="fp-visual__phone">
            <img src={photo} alt="" />
            <div className="fp-visual__tile" />
            <div className="fp-visual__phone-label">iOS · Android</div>
          </div>
        </div>
      )}
    </div>
  );
};

const FeatureRow: React.FC<{
  feature: (typeof keyFeatures)[number];
  reverse: boolean;
  index: number;
}> = ({ feature, reverse, index }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fp-key__row ${reverse ? "reverse" : ""} ${visible ? "visible" : ""}`}>
      <div className="fp-key__copy">
        <span className="fp-key__tag">{feature.tag}</span>
        <h2 className="fp-key__title">{feature.title}</h2>
        <p className="fp-key__body">{feature.body}</p>
        <div className="fp-key__pills">
          <span>Number {String(index + 1).padStart(2, "0")}</span>
          <span>Photo-safe export</span>
        </div>
      </div>
      <div className="fp-key__visual">
        <FeatureVisual variant={feature.visual} photo={feature.photo} />
      </div>
    </div>
  );
};

const KeyFeatures: React.FC = () => {
  return (
    <section className="fp-key">
      <div className="fp-key__list">
        {keyFeatures.map((f, i) => (
          <FeatureRow key={f.title} feature={f} reverse={i % 2 === 1} index={i} />
        ))}
      </div>
    </section>
  );
};

export default KeyFeatures;
